import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/elements/Tabs/Tabs.component';
import { SingleColumnLayout } from '@/layouts/SingleColumnLayout/SingleColumnLayout.component';
import { usePreloadVideoComponent } from './TabsBlock.utils';
import { type IProps, type ITabItem } from './TabsBlock.interface';
import './TabsBlock.styles.css';

/**
 * TabsBlock displays content in a tabbed interface.
 * It utilizes Radix UI Tabs for accessibility and functionality,
 * and renders tab content using SingleColumnLayout.
 */
export const TabsBlock: React.FC<IProps> = ({
  tabs,
  defaultValue,
  orientation = 'horizontal',
  activationMode = 'automatic',
  loop = true,
  className,
  ...blockWrapperProps
}) => {
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [shouldPreloadedId, setShouldPreloadedId] = React.useState<
    string | null
  >(null);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  // Ensure defaultValue is one of the tab values if not provided or invalid
  const currentDefaultValue =
    defaultValue && tabs.some((tab) => tab.value === defaultValue)
      ? defaultValue
      : tabs[0]?.value;

  // Check if tabs list is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (tabsListRef.current && orientation === 'horizontal') {
        const element = tabsListRef.current;
        const isOverflowing = element.scrollWidth > element.clientWidth;
        setIsScrollable(isOverflowing);
      }
    };

    // Check on mount and when window resizes
    checkScrollable();
    window.addEventListener('resize', checkScrollable);

    // Also check after a short delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkScrollable, 100);

    return () => {
      window.removeEventListener('resize', checkScrollable);
      clearTimeout(timeoutId);
    };
  }, [tabs, orientation]);

  usePreloadVideoComponent({ tabs, setShouldPreloadedId });

  return (
    <BlockWrapper
      {...blockWrapperProps}
      className={clsx('tabsBlock', className)}
    >
      <ContentWrapper>
        <ContentColumn>
          <Tabs
            defaultValue={currentDefaultValue}
            orientation={orientation}
            activationMode={activationMode}
            className="tabsBlock__root"
          >
            <TabsList
              ref={tabsListRef}
              loop={loop}
              aria-label="Dynamic tabs"
              className={clsx(
                'tabsBlock__list',
                isScrollable &&
                  orientation === 'horizontal' &&
                  'tabsBlock__list--scrollable',
              )}
            >
              {tabs.map((tab: ITabItem, index) => (
                <TabsTrigger
                  key={`tabs-content-trigger-${tab.value}-${index}`}
                  value={tab.value}
                  className="tabsBlock__trigger"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((tab: ITabItem, index) => (
              <TabsContent
                key={`tabs-content-content-${tab.value}-${index}`}
                value={tab.value}
                className="tabsBlock__content"
                {...(tab.value === shouldPreloadedId
                  ? { forceMount: true }
                  : {})}
              >
                <SingleColumnLayout items={tab.items} />
              </TabsContent>
            ))}
          </Tabs>
        </ContentColumn>
      </ContentWrapper>
    </BlockWrapper>
  );
};
